import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { searchPageContent } from "@/api/contentSearch";
import type {
  PageContentSearchPayload,
  PageContentSearchResult,
  PageContentSearchSpec
} from "@/types/pageContentSearch";
import "@/styles/page-content-search.css";

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function highlightText(text: string, terms: string[]): React.ReactNode {
  const src = String(text || "");
  if (!src || !terms.length) return src;
  const sorted = [...terms].filter((t) => t.length >= 2).sort((a, b) => b.length - a.length);
  if (!sorted.length) return src;
  const re = new RegExp(`(${sorted.map(escapeRegExp).join("|")})`, "gi");
  const parts = src.split(re);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <mark key={i}>{part}</mark>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    )
  );
}

function fieldBadge(label: string, on: boolean) {
  return (
    <span className={"creaPageSearchFieldBadge" + (on ? " on" : "")} title={on ? "Matched" : "No match"}>
      {label}
    </span>
  );
}

function formatBytes(n: number): string {
  const x = Number(n) || 0;
  if (x >= 1_000_000) return `${(x / 1_000_000).toFixed(1)} MB`;
  if (x >= 1000) return `${(x / 1000).toFixed(1)} kB`;
  return `${x} B`;
}

function ResultCard({
  row,
  terms,
  defaultOpen,
  onOpenStats,
  onView
}: {
  row: PageContentSearchResult;
  terms: string[];
  defaultOpen?: boolean;
  onOpenStats?: (path: string) => void;
  onView?: (path: string) => void;
}) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  const mf = row.matchFields || {};

  return (
    <article className="creaPageSearchResult">
      <div
        className="creaPageSearchResultHead"
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="creaPageSearchResultTitle">{row.title || row.path}</div>
          <div className="creaPageSearchResultPath">{row.path}</div>
          <div className="creaPageSearchFieldBadges">
            {fieldBadge("Title", Boolean(mf.title))}
            {fieldBadge("Meta", Boolean(mf.meta))}
            {fieldBadge("Headings", Boolean(mf.headings))}
            {fieldBadge("Path", Boolean(mf.path))}
            {fieldBadge("Body", Boolean(mf.body))}
          </div>
        </div>
        <span className="creaPageSearchScore" title="Relevance score">
          {row.score}
        </span>
      </div>
      {open ? (
        <div className="creaPageSearchResultBody">
          {row.metaDescription ? (
            <div className="creaPageSearchSnippet">
              <div className="creaPageSearchSnippetLabel">Meta description</div>
              {highlightText(row.metaDescription, terms)}
            </div>
          ) : null}
          {(row.snippets || []).map((sn, si) => (
            <div className="creaPageSearchSnippet" key={si}>
              <div className="creaPageSearchSnippetLabel">
                {sn.field}
                {sn.term ? ` · “${sn.term}”` : ""}
              </div>
              {highlightText(sn.text, terms)}
            </div>
          ))}
          <div className="creaPageSearchMeta" style={{ marginTop: 10 }}>
            {row.matchedTerms?.length ? (
              <span>Matched: {row.matchedTerms.join(", ")}</span>
            ) : null}
            {row.wordCount != null ? <span>{row.wordCount.toLocaleString()} words</span> : null}
            {row.cacheBytes != null ? <span>{formatBytes(row.cacheBytes)} cached</span> : null}
          </div>
          <div className="creaPageSearchActions">
            <button type="button" className="btn" onClick={() => onView?.(row.path)}>
              View live
            </button>
            <button type="button" className="btn" onClick={() => onOpenStats?.(row.path)}>
              Page stats
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                try {
                  void navigator.clipboard.writeText(row.path);
                } catch {
                  /* ignore */
                }
              }}
            >
              Copy path
            </button>
          </div>
        </div>
      ) : null}
    </article>
  );
}

type Props = {
  open: boolean;
  domain: string;
  initialQuery?: string;
  /** Pre-loaded result from assistant (skip initial fetch). */
  spec?: PageContentSearchSpec | null;
  onClose: () => void;
  onOpenStats?: (path: string) => void;
};

export default function PageContentSearchModal({
  open,
  domain,
  initialQuery = "",
  spec,
  onClose,
  onOpenStats
}: Props) {
  const [query, setQuery] = useState(initialQuery);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState<PageContentSearchPayload | null>(spec?.payload ?? null);

  const runSearch = useCallback(
    async (q: string) => {
      const text = String(q || "").trim();
      if (text.length < 2) {
        setError("Enter at least 2 characters");
        return;
      }
      if (!domain) return;
      setLoading(true);
      setError("");
      try {
        const out = await searchPageContent(domain, text, { limit: 30, expandSynonyms: true });
        setData(out);
      } catch (e) {
        setData(null);
        setError(String(e instanceof Error ? e.message : e));
      } finally {
        setLoading(false);
      }
    },
    [domain]
  );

  useEffect(() => {
    if (!open) return;
    setQuery(initialQuery || spec?.payload?.query || "");
    setData(spec?.payload ?? null);
    setError("");
    if (spec?.payload?.ok !== false && spec?.payload?.results) return;
    if ((initialQuery || spec?.payload?.query || "").trim().length >= 2) {
      void runSearch(initialQuery || spec?.payload?.query || "");
    }
  }, [open, initialQuery, spec, runSearch]);

  useEffect(() => {
    if (!open) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const terms = useMemo(() => data?.expandedTerms || [], [data?.expandedTerms]);

  if (!open) return null;

  const results = data?.results || [];

  return createPortal(
    <div
      className="creaPageSearchModal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="crea-page-search-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="creaPageSearchDialog" onClick={(e) => e.stopPropagation()}>
        <div className="creaPageSearchHead">
          <div>
            <strong id="crea-page-search-title">Page content search</strong>
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              Full-text search across cached visitor HTML on <span className="mono">{domain}</span>
            </div>
            {data && data.ok !== false ? (
              <div className="creaPageSearchMeta">
                <span>
                  {data.matchCount ?? results.length} match{(data.matchCount ?? 0) === 1 ? "" : "es"}
                </span>
                <span>{data.scannedPages?.toLocaleString() ?? "—"} pages scanned</span>
                {data.tookMs != null ? <span>{data.tookMs} ms</span> : null}
              </div>
            ) : null}
            {terms.length > 0 ? (
              <div className="creaPageSearchTerms" title="Search terms including synonyms">
                {terms.slice(0, 24).map((t) => (
                  <span key={t} className="creaPageSearchTermChip">
                    {t}
                  </span>
                ))}
                {terms.length > 24 ? (
                  <span className="creaPageSearchTermChip">+{terms.length - 24}</span>
                ) : null}
              </div>
            ) : null}
          </div>
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="creaPageSearchToolbar">
          <input
            className="search mono"
            placeholder="e.g. WordPress development, maintenance plans…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void runSearch(query);
            }}
          />
          <button type="button" className="btn primary" disabled={loading} onClick={() => void runSearch(query)}>
            {loading ? "Searching…" : "Search"}
          </button>
        </div>

        {error ? <div className="creaPageSearchError">{error}</div> : null}

        <div className="creaPageSearchBody">
          {loading && !results.length ? (
            <div className="creaPageSearchEmpty">Searching cached pages…</div>
          ) : null}
          {!loading && data && results.length === 0 ? (
            <div className="creaPageSearchEmpty">
              No pages matched. Try broader terms or check that pages are cached.
            </div>
          ) : null}
          {results.map((row, i) => (
            <ResultCard
              key={row.path}
              row={row}
              terms={terms.length ? terms : [data?.query || ""]}
              defaultOpen={i < 3}
              onOpenStats={onOpenStats}
              onView={(p) => window.open(`https://${domain}${p}`, "_blank")}
            />
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

export function pageContentSearchLabel(spec: PageContentSearchSpec): string {
  const q = spec.payload?.query || "Search";
  const n = spec.payload?.matchCount ?? spec.payload?.results?.length ?? 0;
  return `${q} (${n} pages)`;
}
